/** @format */
import { css, keyframes } from 'styled-components';

/*
 * A duplicate of card-hover.ts's role, not its content — see the comment at
 * the top of that file for why a shared visual treatment lives here rather
 * than in each caller: the model page and the product page's insights
 * action both need to say "this is new" the same way, and this experiment's
 * own folder is where that shared piece belongs, so it is deleted with the
 * rest of the experiment.
 */

/**
 * A ring that expands out of the element it is applied to and fades, over
 * and over.
 *
 * It runs for as long as the page is open, which is the point: the action is
 * new and nothing else nearby is competing for the same attention. Most of
 * each cycle is the ring already fading, and it rests at nothing before
 * starting again, so it reads as a repeated beat rather than as something
 * permanently lit.
 */
const newActionPulse = keyframes`
    from {
        transform: scale(0.9);
        opacity: 0.7;
    }

    70% {
        opacity: 0;
    }

    to {
        transform: scale(2);
        opacity: 0;
    }
`;

/**
 * Marks an element as the new thing in whatever row or line it sits in.
 *
 * The ring is drawn on the element's own pseudo-element rather than a
 * descendant, so each caller decides what carries it — the model page's
 * hero action puts it on the icon inside a grid of equal columns, where a
 * ring that took up space would move the actions either side of it; a
 * caller with no such neighbors can put it directly on the element it
 * highlights.
 *
 * A reader who has asked for reduced motion is given the ring standing
 * still instead, since what it is saying — this one is new — is worth
 * saying without the movement that would otherwise say it.
 */
export const newActionHighlight = css`
    position: relative;

    &:after {
        content: '';
        position: absolute;
        inset: calc(-1 * var(--layout-spacing-4));
        border: 0.2rem solid var(--colors-brand-secondary);
        border-radius: var(--effects-radius-full);
        animation: ${newActionPulse} 1.6s
            var(--animations-default-timing-function) infinite;
        pointer-events: none;
    }

    @media (prefers-reduced-motion: reduce) {
        &:after {
            animation: none;
            opacity: 0.7;
        }
    }
`;
